import dayjs from "dayjs";
import nodeAPI from "../../service/ApiServiceNode";
import { API_CHECKLIST_TASK } from "../../constants";
import { createAuthorizedEventSource } from "../../utils/sseClient";
import { TASK_STATUS, isApprovalPendingStatus } from "../../components/Tasks/taskLifecycle";

const DEFAULT_SCOPE = "all";
const DEFAULT_MINUTES = 180;
const STREAM_TIMEOUT_MS = 120000;
let openedTaskWindow = null;

function unwrap(response) {
    return response?.data?.data || response?.data || {};
}

function apiUrl(path) {
    if (/^https?:\/\//i.test(path)) return path;
    const base = nodeAPI.defaults.baseURL || "";
    return `${base.replace(/\/+$/, "")}/${String(path).replace(/^\/+/, "")}`;
}

function parseEventData(event) {
    try {
        return JSON.parse(event.data || "{}");
    } catch {
        return {};
    }
}

function allRanked(result) {
    return Array.isArray(result?.ranked_tasks) ? result.ranked_tasks : [];
}

function findRankedTask(result, id) {
    return allRanked(result).find((task) => String(task.id) === String(id)) || null;
}

function activeResult(ctx) {
    return ctx.store.getState().triage?.result || null;
}

function topTasksPayload(result) {
    return {
        summary: result?.summary || {},
        now: result?.now || [],
        next: result?.next || [],
        plan_blocks: result?.plan_blocks || []
    };
}

function listenToJob(sseUrl, ctx) {
    return new Promise((resolve, reject) => {
        const eventSource = createAuthorizedEventSource(apiUrl(sseUrl));
        let settled = false;
        let timeout = null;

        const finish = (ok, value) => {
            if (settled) return;
            settled = true;
            if (timeout) clearTimeout(timeout);
            eventSource.close();
            if (ok) {
                resolve(value);
            } else {
                reject(value);
            }
        };

        timeout = setTimeout(() => {
            finish(false, new Error("Task triage timed out"));
        }, STREAM_TIMEOUT_MS);

        eventSource.addEventListener("progress", (event) => {
            ctx.actions.appendTriageProgress(parseEventData(event));
        });

        eventSource.addEventListener("result", (event) => {
            const result = parseEventData(event);
            ctx.actions.setTriageResult(result);
            finish(true, result);
        });

        eventSource.addEventListener("failed", (event) => {
            const data = parseEventData(event);
            finish(false, new Error(data.message || "Task triage failed"));
        });

        eventSource.onerror = () => {
            if (!settled && eventSource.readyState === 2) {
                finish(false, new Error("Task triage stream disconnected"));
            }
        };
    });
}

async function pollJob(jobId, ctx) {
    for (let attempt = 0; attempt < 40; attempt += 1) {
        const response = await nodeAPI.get(`${API_CHECKLIST_TASK}/triage/jobs/${encodeURIComponent(jobId)}`);
        const job = unwrap(response).job || unwrap(response);
        const progress = Array.isArray(job.progress) ? job.progress : [];
        for (const item of progress.slice(ctx.store.getState().triage.progress.length)) {
            ctx.actions.appendTriageProgress(item);
        }
        if (job.status === "complete" && job.result) {
            ctx.actions.setTriageResult(job.result);
            return job.result;
        }
        if (job.status === "failed") {
            throw new Error(job.error || "Task triage failed");
        }
        await new Promise((resolve) => setTimeout(resolve, 750));
    }
    throw new Error("Task triage polling timed out");
}

async function analyze(args, ctx) {
    const scope = args.scope || DEFAULT_SCOPE;
    const availableMinutes = Number(args.available_minutes) || DEFAULT_MINUTES;
    ctx.actions.setTriageStatus("running", "Analyzing tasks...");

    try {
        const response = await nodeAPI.post(`${API_CHECKLIST_TASK}/triage/analyze`, {
            scope,
            available_minutes: availableMinutes
        });
        const data = unwrap(response);
        const jobId = data.job_id || data.job?.job_id;
        const sseUrl = data.sse_url;
        if (!jobId || !sseUrl) {
            throw new Error("Unexpected task triage response");
        }
        ctx.actions.setTriageJob(jobId);

        let result;
        try {
            result = await listenToJob(sseUrl, ctx);
        } catch {
            result = await pollJob(jobId, ctx);
        }

        if (!ctx.client?.isRealtimeOpen?.()) {
            ctx.actions.setConnection("paused", "Analysis ready. Click Resume voice to discuss it.");
        }
        return {
            ok: true,
            status: "analysis_ready",
            ...topTasksPayload(result)
        };
    } catch (error) {
        const detail =
            error?.response?.data?.data?.message ||
            error?.response?.data?.message ||
            error?.message ||
            "Task triage failed";
        ctx.actions.setTriageStatus("error", detail);
        if (!ctx.client?.isRealtimeOpen?.()) {
            ctx.actions.setConnection("paused", `Task triage failed: ${detail}. Click Resume voice to try again.`);
        }
        return { ok: false, detail };
    }
}

async function planDay(args, ctx) {
    let result = activeResult(ctx);
    if (!result) {
        const analyzed = await analyze(args, ctx);
        result = activeResult(ctx);
        return { ...analyzed, plan_blocks: result?.plan_blocks || [] };
    }

    const availableMinutes = Number(args.available_minutes) || DEFAULT_MINUTES;
    const blocks = [];
    let remaining = Math.min(480, Math.max(30, availableMinutes));
    for (const task of allRanked(result).slice(0, 8)) {
        if (remaining <= 0) break;
        const minutes = Math.min(remaining, task.relation === "verification" ? 20 : task.score >= 70 ? 60 : 35);
        blocks.push({
            task_id: task.id,
            task_name: task.name,
            minutes,
            action:
                task.relation === "verification"
                    ? "verify"
                    : task.relation === "delegated"
                      ? "follow_up"
                      : "work"
        });
        remaining -= minutes;
    }
    ctx.actions.setTriagePlanBlocks(blocks);
    return { ok: true, plan_blocks: blocks };
}

function openWindow(url) {
    if (typeof window === "undefined") return { opened: false, url };
    const absolute = url.startsWith("/") ? `${window.location.origin}${url}` : url;
    const opened = window.open(absolute, "hrp_voice_task_triage");
    if (opened) {
        openedTaskWindow = opened;
        opened.focus?.();
        return { opened: true, url: absolute };
    }
    return { opened: false, url: absolute };
}

function openRankedQueue(_args, ctx) {
    const result = activeResult(ctx);
    const first = result?.now?.[0] || result?.ranked_tasks?.[0] || null;
    if (first) {
        ctx.actions.setTriageSelectedTask(first);
    }
    return { ok: true, ...openWindow("/tasks/my-tasks"), selected_task: first };
}

function openTask({ id }, ctx) {
    if (!id) return { ok: false, detail: "id is required" };
    const task = findRankedTask(activeResult(ctx), id);
    if (task) {
        ctx.actions.setTriageSelectedTask(task);
    }
    return { ok: true, task, ...openWindow(task?.url || `/tasks/my-tasks?taskId=${encodeURIComponent(id)}`) };
}

function closeWindow() {
    if (!openedTaskWindow || openedTaskWindow.closed) {
        return { ok: true, closed: false, detail: "No voice-opened task window is active." };
    }
    openedTaskWindow.close();
    openedTaskWindow = null;
    return { ok: true, closed: true };
}

async function deferTask({ id, days }, ctx) {
    if (!id) return { ok: false, detail: "id is required" };
    const task = findRankedTask(activeResult(ctx), id);
    const deferDays = Math.min(90, Math.max(1, Number(days) || 7));
    const dueDate = dayjs().add(deferDays, "day").format("YYYY-MM-DD");
    const response = await nodeAPI.put(`${API_CHECKLIST_TASK}/${encodeURIComponent(id)}`, {
        due_date: dueDate
    });
    ctx.actions.patchTriageTask(id, {
        due_date: dueDate,
        due_in_days: deferDays,
        reasons: [...(task?.reasons || []), `deferred ${deferDays} days`]
    });
    return { ok: true, due_date: dueDate, task: unwrap(response) };
}

async function markDone({ id }, ctx) {
    if (!id) return { ok: false, detail: "id is required" };
    const task = findRankedTask(activeResult(ctx), id);
    const isVerificationApproval = task?.relation === "verification" || isApprovalPendingStatus(task?.status);
    const status = isVerificationApproval || !task?.verifier_id ? TASK_STATUS.DONE : TASK_STATUS.IN_REVIEW;
    const response = await nodeAPI.put(`${API_CHECKLIST_TASK}/${encodeURIComponent(id)}`, {
        status
    });
    ctx.actions.patchTriageTask(id, { status });
    return {
        ok: true,
        status,
        detail: status === TASK_STATUS.IN_REVIEW ? "Task submitted for verification." : "Task marked done.",
        task: unwrap(response)
    };
}

function explainRanking({ id }, ctx) {
    if (!id) return { ok: false, detail: "id is required" };
    const task = findRankedTask(activeResult(ctx), id);
    if (!task) return { ok: false, detail: "Task is not in the current triage result." };
    return {
        ok: true,
        task_id: task.id,
        task_name: task.name,
        score: task.score,
        reasons: task.reasons || [],
        relation: task.relation,
        due_in_days: task.due_in_days
    };
}

export const triageTools = [
    {
        key: "task_triage.analyze",
        schema: {
            type: "function",
            description: "Analyze and rank the user's active tasks. This pauses voice while SSE progress runs.",
            parameters: {
                type: "object",
                properties: {
                    scope: {
                        type: "string",
                        enum: ["all", "assigned", "verification", "delegated"]
                    },
                    available_minutes: { type: "number" }
                }
            }
        },
        executor: analyze
    },
    {
        key: "task_triage.plan_day",
        schema: {
            type: "function",
            description: "Create a time-boxed day plan from the current ranked task queue.",
            parameters: {
                type: "object",
                properties: {
                    available_minutes: { type: "number" }
                }
            }
        },
        executor: planDay
    },
    {
        key: "task_triage.open_ranked_queue",
        schema: {
            type: "function",
            description: "Open the ranked task queue window or focus the triage panel.",
            parameters: { type: "object", properties: {} }
        },
        executor: openRankedQueue
    },
    {
        key: "task_triage.open_task",
        schema: {
            type: "function",
            description: "Open or focus one task from the ranked triage result.",
            parameters: {
                type: "object",
                properties: { id: { type: "number" } },
                required: ["id"]
            }
        },
        executor: openTask
    },
    {
        key: "task_triage.close_window",
        schema: {
            type: "function",
            description: "Close the task window opened by the task triage agent.",
            parameters: { type: "object", properties: {} }
        },
        executor: closeWindow
    },
    {
        key: "task_triage.defer_task",
        schema: {
            type: "function",
            description: "After explicit confirmation, move a task due date forward by a number of days.",
            parameters: {
                type: "object",
                properties: {
                    id: { type: "number" },
                    days: { type: "number" }
                },
                required: ["id"]
            }
        },
        executor: deferTask
    },
    {
        key: "task_triage.mark_done",
        schema: {
            type: "function",
            description: "After explicit confirmation, mark a task done or submit it for verification.",
            parameters: {
                type: "object",
                properties: { id: { type: "number" } },
                required: ["id"]
            }
        },
        executor: markDone
    },
    {
        key: "task_triage.explain_ranking",
        schema: {
            type: "function",
            description: "Explain why a task received its current triage rank.",
            parameters: {
                type: "object",
                properties: { id: { type: "number" } },
                required: ["id"]
            }
        },
        executor: explainRanking
    }
];
